import React from 'react';
import AgentStatusCard from './cards/AgentStatusCard';
import TasksOverviewCard from './cards/TasksOverviewCard';
import CostsOverviewCard from './cards/CostsOverviewCard';
import RecentActivityCard from './cards/RecentActivityCard';
import { useDashboardData } from '../hooks/useDashboardData';

function Dashboard() {
  const { data, loading, error, lastUpdated, refetch } = useDashboardData(30000); // Poll every 30s

  const getAgentStatus = (agent: { status: string; runningCount: number; desiredCount: number }): 'online' | 'offline' | 'busy' | 'not_deployed' | 'running' | 'degraded' | 'starting' => {
    // Server now returns: running, not_deployed, offline, degraded, starting
    if (agent.status === 'running') return 'running';
    if (agent.status === 'not_deployed') return 'not_deployed';
    if (agent.status === 'degraded') return 'degraded';
    if (agent.status === 'starting') return 'starting';
    // Legacy support
    if (agent.status === 'ACTIVE' && agent.runningCount > 0) return 'online';
    if (agent.status === 'ACTIVE' && agent.runningCount === 0) return 'offline';
    return 'offline';
  };

  const getLastActive = (agent: { status: string; runningCount: number; lastEvent?: string }) => {
    if (agent.status === 'running' || agent.runningCount > 0) {
      return 'Running now';
    } else if (agent.status === 'not_deployed') {
      return 'Not deployed';
    } else if (agent.status === 'starting') {
      return 'Starting up...';
    } else if (agent.status === 'degraded') {
      return 'Partially running';
    } else if (agent.lastEvent) {
      return agent.lastEvent.substring(0, 50) + '...';
    }
    return 'Not deployed';
  };

  // Task stats from Notion data
  const taskStats = React.useMemo(() => {
    if (!data?.tasks?.tasks) return { q1: 0, q2: 0, q3: 0, q4: 0, total: 0 };

    const tasks = data.tasks.tasks;
    const stats = { q1: 0, q2: 0, q3: 0, q4: 0 };

    tasks.forEach(task => {
      if (task.quadrant?.includes('Q1')) stats.q1++;
      else if (task.quadrant?.includes('Q2')) stats.q2++;
      else if (task.quadrant?.includes('Q3')) stats.q3++;
      else stats.q4++;
    });

    return { ...stats, total: tasks.length };
  }, [data?.tasks?.tasks]);

  // Project count (unique areas)
  const projectCount = React.useMemo(() => {
    if (!data?.tasks?.tasks) return 0;
    const areas = new Set(data.tasks.tasks.map(t => t.area).filter(Boolean));
    return areas.size || 3; // Default to 3 if no data
  }, [data?.tasks?.tasks]);

  return (
    <div>
      <header className="mb-8 flex justify-between items-start">
        <div>
          <h1 className="text-3xl font-bold">Dashboard</h1>
          <p className="text-mc-muted mt-2">Overview of your MarketBrewer agents</p>
        </div>
        <div className="text-right">
          <button
            onClick={refetch}
            className="text-mc-accent text-sm hover:underline"
            disabled={loading}
          >
            {loading ? 'Refreshing...' : 'Refresh'}
          </button>
          {lastUpdated && (
            <p className="text-xs text-mc-muted mt-1">
              Last updated: {lastUpdated.toLocaleTimeString()}
            </p>
          )}
        </div>
      </header>

      {error && (
        <div className="mb-6 p-4 bg-red-900/20 border border-red-500 rounded-lg text-red-400">
          Error loading data: {error}
        </div>
      )}

      {/* Agent Status Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        <AgentStatusCard
          name="Moltbot"
          role="Gateway / Orchestrator"
          status={data?.agents ? getAgentStatus(data.agents.moltbot) : 'offline'}
          lastActive={data?.agents ? getLastActive(data.agents.moltbot) : 'Loading...'}
          loading={loading}
        />
        <AgentStatusCard
          name="Architect"
          role="24/7 Coding Agent"
          status={data?.agents ? getAgentStatus(data.agents.architect) : 'offline'}
          lastActive={data?.agents ? getLastActive(data.agents.architect) : 'Loading...'}
          loading={loading}
        />
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
        <TasksOverviewCard
          stats={taskStats}
          loading={loading}
          error={data?.tasks?.error}
        />
        <CostsOverviewCard
          costs={data?.costs || { mtd: 0, projected: 0, error: null }}
          loading={loading}
        />
        <div className="card">
          <h3 className="text-lg font-semibold mb-4">Projects</h3>
          {loading ? (
            <div className="animate-pulse bg-mc-border h-10 w-20 rounded"></div>
          ) : (
            <>
              <div className="text-4xl font-bold text-mc-accent">{projectCount}</div>
              <p className="text-mc-muted text-sm mt-2">Active areas</p>
            </>
          )}
        </div>
      </div>

      {/* Recent Activity */}
      <RecentActivityCard
        events={data?.activity?.events || []}
        loading={loading}
        error={data?.activity?.error}
      />
    </div>
  );
}

export default Dashboard;
